// Geometry helpers for manual highlight bands and for moving/hit-testing both
// manual and OCR bands. All coordinates are normalized (0..1) so they overlay
// any rendered size of the image and export at full resolution.

// Build a tilted band (4-point quad) from a drag: A→B is the band centre line,
// so the drag direction sets the angle and the drag length sets the size. The
// band extends `thickness/2` perpendicular to each side.
export function makeBandFromDrag(ax, ay, bx, by, thickness) {
  const dx = bx - ax;
  const dy = by - ay;
  const length = Math.hypot(dx, dy);
  // Degenerate drag (a click) → a small axis-aligned square so it's visible.
  if (length < 1e-4) {
    const h = thickness / 2;
    return [
      { x: ax - h, y: ay - h },
      { x: ax + h, y: ay - h },
      { x: ax + h, y: ay + h },
      { x: ax - h, y: ay + h },
    ];
  }
  const nx = (-dy / length) * (thickness / 2);
  const ny = (dx / length) * (thickness / 2);
  return [
    { x: ax + nx, y: ay + ny },
    { x: bx + nx, y: by + ny },
    { x: bx - nx, y: by - ny },
    { x: ax - nx, y: ay - ny },
  ];
}

export function translatePolygon(vertices, dx, dy) {
  return vertices.map((vertex) => ({ x: vertex.x + dx, y: vertex.y + dy }));
}

export function pointInPolygon(vertices, px, py) {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i].x;
    const yi = vertices[i].y;
    const xj = vertices[j].x;
    const yj = vertices[j].y;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function clampVertices(vertices) {
  return vertices.map((vertex) => ({
    x: Math.min(1, Math.max(0, vertex.x)),
    y: Math.min(1, Math.max(0, vertex.y)),
  }));
}

// Apply per-row edits (move offset / delete) to OCR rows, returning the rows as
// they should be rendered/hit-tested/exported. Deleted rows are dropped.
export function applyBandEdits(rows, bandEdits) {
  if (!bandEdits) return rows;
  return rows
    .map((row) => {
      const edit = bandEdits[row.rowId];
      if (!edit) return row;
      if (edit.deleted) return null;
      if (edit.dx || edit.dy) {
        return {
          ...row,
          normalizedPolygons: (row.normalizedPolygons || []).map((polygon) =>
            translatePolygon(polygon, edit.dx, edit.dy),
          ),
          normalizedVertices: row.normalizedVertices
            ? translatePolygon(row.normalizedVertices, edit.dx, edit.dy)
            : row.normalizedVertices,
        };
      }
      return row;
    })
    .filter(Boolean);
}

let manualBandSeq = 0;
export function nextManualBandId() {
  manualBandSeq += 1;
  return `manual-${manualBandSeq}`;
}
